//src/services/clientCompanies.ts
import { api } from "@/lib/apiClient";

export type ListClientCompaniesParams = {
  q?: string;
  limit?: number;
  offset?: number;
};

export type ClientCompanyListItem = {
  id: string;
  tenant_id: string;
  person_id: string;
  name: string;
  legal_name?: string | null;
  document_number?: string | null;
  slug: string;
  domain?: string | null;
  state_registration?: string | null;
  municipal_registration?: string | null;
  primary_color: string;
  secondary_color: string;
  timezone: string;
  locale: string;
  is_active: boolean;
  document_storage_limit_mb: number;
  tenant_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ClientCompanyContactOut = {
  id: string;
  kind: "email" | "phone" | "whatsapp" | "other";
  value: string;
  is_primary: boolean;
  created_at?: string;
};

export type ClientCompanyAddressOut = {
  id: string;
  label?: string | null;
  zip?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  is_primary: boolean;
  created_at?: string;
};

export type ClientCompanyDetailOut = {
  client_company: {
    id: string;
    tenant_id: string;
    person_id: string;
    slug: string;
    domain?: string | null;
    state_registration?: string | null;
    municipal_registration?: string | null;
    primary_color: string;
    secondary_color: string;
    timezone: string;
    locale: string;
    is_active: boolean;
    document_storage_limit_mb: number;
    created_at?: string | null;
    updated_at?: string | null;
  };
  person: {
    id: string;
    person_type: "PF" | "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    is_active: boolean;
    created_at?: string | null;
    updated_at?: string | null;
    photo?: {
      base64: string;
      mime_type: string;
    } | null;
    photo_file_name?: string | null;
    photo_size_bytes?: number | null;
  };
  contacts: ClientCompanyContactOut[];
  addresses: ClientCompanyAddressOut[];
};


export type ClientCompanyUpsertIn = {
  tenant_id?: string | null;
  person: {
    person_type: "PJ";
    name: string;
    legal_name?: string | null;
    document_number?: string | null;
    photo?: {
      photo_base64?: string | null;
      photo_mime_type?: string | null;
      photo_file_name?: string | null;
    } | null;
    contacts: Array<{
      kind: "email" | "phone" | "whatsapp" | "other";
      value: string;
      is_primary: boolean;
    }>;
    addresses: Array<{
      label?: string | null;
      zip?: string | null;
      street?: string | null;
      number?: string | null;
      complement?: string | null;
      district?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      is_primary: boolean;
    }>;
  };
  slug: string;
  domain?: string | null;
  state_registration?: string | null;
  municipal_registration?: string | null;
  primary_color?: string;
  secondary_color?: string;
  timezone?: string;
  locale?: string;
  is_active?: boolean;
  document_storage_limit_mb?: number;
};

export async function listClientCompanies(params: ListClientCompaniesParams = {}) {
  const { q = "", limit = 50, offset = 0 } = params;

  return api.get<ClientCompanyListItem[]>("/client-companies", {
    params: { q, limit, offset },
  });
}

export async function listAvailableClientCompanies(tenantId?: string | null) {
  return api.get<ClientCompanyListItem[]>("/client-companies/available", {
    params: tenantId ? { tenant_id: tenantId } : undefined,
  });
}

export async function getClientCompanyDetail(clientCompanyId: string) {
  return api.get<ClientCompanyDetailOut>(`/client-companies/${clientCompanyId}`);
}

export async function createClientCompany(payload: ClientCompanyUpsertIn) {
  return api.post<{ id: string }>("/client-companies", payload);
}

export async function updateClientCompany(clientCompanyId: string, payload: ClientCompanyUpsertIn) {
  return api.put<{ id: string }>(`/client-companies/${clientCompanyId}`, payload);
}

export async function setClientCompanyActive(clientCompanyId: string, is_active: boolean) {
  return api.patch<{ id: string }>(`/client-companies/${clientCompanyId}`, { is_active });
}
